<template>
  <v-card class="step-tab-panel" flat>
    <v-container class="pa-0 ma-0 variety-container d-flex flex-column">
      <v-row class="header-container flex-grow-0 ma-0">
        <v-col>
          <span data-id-e2e="varietyGroupsInfoNote">{{ $t('varietyGroups.infoNote') }}</span>

          <!-- Variety groups tooltip -->
          <DocsLink link="toolguide/070-varietygroups.html" />
        </v-col>
      </v-row>
      <div class="main-panel no-gutters d-flex flex-row flex-grow-1" no-gutters>
        <v-col class="d-flex flex-column" cols="2">
          <CannGroupMenu
            :selected-cann-group="selectedCannGroup"
            :preselect-cann-group="false"
            @on-cann-group-selected="selectCannGroup"
          >
            <template #tickbox="props">
              <v-icon v-if="existsInVarietyGroups(props.cgKey)" class="float-right" size="30">
                $success
              </v-icon>
              <v-icon v-else color="#d3d3d3" class="float-right">
                mdi-checkbox-blank-circle
              </v-icon>
            </template>
          </CannGroupMenu>
        </v-col>
        <v-col cols="6" class="variety-attributes d-flex flex-column">
          <v-row class="variety-attributes-title flex-grow-0">
            <v-col>{{ $t('varietyGroups.settings') }}</v-col>
          </v-row>
          <div class="variety-attributes-container">
            <VarietyAttributes ref="varietyAttributes" :selected-cann-group="selectedCannGroup" />
          </div>
        </v-col>
        <v-col cols="4" />
      </div>
      <div data-id-e2e="btnConfirmVarietyGroups" class="page-actions-container">
        <PageActions
          :has-data-changes="true"
          :has-data-errors="hasDataErrors"
          save-btn-text="confirmAsSet"
          :show-discard="false"
          :save-disabled="isEditingDisabled"
          :is-discard-enabled="!isEditingDisabled"
          @save="saveAll"
        />
      </div>
    </v-container>

    <UnsavedDataModal
      ref="unsavedDataModal"
      :value="isUnsavedDataModalOpen"
      @cancel="closeUnsavedDataModal"
      @confirm="confirmUnsavedDataModal"
    />
  </v-card>
</template>

<script>
import { getCurrentInstance, nextTick } from 'vue';
import { useUnsavedDataWarning } from '@/composables/unsaved-data-warning';
import { usePermission } from '@/composables/use-permission';
import { useFeature } from '@/composables/use-feature';
import { useScenarioStatusFormatter } from '@/composables/use-scenario-status-formatter';
import { useAiAgentActions } from '@/composables/use-ai-agent-actions';
import { useScenariosStore } from '@/stores/scenarios';
import { useAiStore } from '@/stores/ai';
import { mapState } from 'pinia';

export default {
  beforeRouteLeave(to, from, next) {
    this.beforeRouteLeave(to, from, next);
  },
  setup() {
    const instance = getCurrentInstance();
    const { canEditVarietyPage } = usePermission();
    const { aiPanelEnabled } = useFeature();
    const { formatScenarioProgress } = useScenarioStatusFormatter('variety-groups');
    useAiAgentActions({
      allowScenarioWorkflowNavigation: true,
      getExclude: () =>
        canEditVarietyPage.value
          ? []
          : [
              'move-attribute',
              'save-variety-group',
              'discard-variety-group',
              'save-all-variety-groups',
            ],
      getSupplementary: () => {
        const attrs = instance.proxy.$refs?.varietyAttributes;
        const moveAttributeContext = attrs
          ? `Current assignments — Unselected: ${attrs.unusedAttributes?.map(a => a.name).join(', ') || 'None'}. ` +
            `Remain constant: ${attrs.remainConstantAttributes?.map(a => a.name).join(', ') || 'None'}. ` +
            `Differ: ${attrs.canDifferAttributes?.map(a => a.name).join(', ') || 'None'}.`
          : 'No cann group selected.';
        const cannGroups = instance.proxy.selectedScenario?.cannGroups || [];
        const flattenLeafNames = nodes =>
          nodes.flatMap(node =>
            !node.children?.length ? [node.name] : flattenLeafNames(node.children)
          );
        const cannGroupNames = flattenLeafNames(cannGroups).join(', ') || 'None';
        return {
          'move-attribute': moveAttributeContext,
          'select-cann-group': `Available leaf cann groups: ${cannGroupNames}`,
        };
      },
      actions: {
        'move-attribute': {
          description:
            'Set attribute assignments for the currently selected cann group. ' +
            'Separate multiple targets with ";". Each section uses "target=Attr1,Attr2" format. ' +
            'Targets are: "unselected", "remain-constant", "differ". ' +
            'Only include targets you want to change. Only use attribute names exactly as listed in the available attributes list in the context. ' +
            'Do not confirm success in your message text; the system will confirm or report errors after the action completes.',
          workingLabel: 'Updating variety group',
          confirmationLabel: 'update variety group attribute assignments',
          parameters: {
            type: 'object',
            properties: {
              value: {
                type: 'string',
                description:
                  'Attribute assignments encoded as: differ=Attr1,Attr2;remain-constant=Attr3 — semicolon-separated target=name1,name2 pairs.',
              },
            },
            required: ['value'],
          },
          handler: async ({ value }) => {
            if (!canEditVarietyPage.value)
              return '[Tool] Action failed: you do not have permission to edit variety groups. Inform the user.';
            // Format: differ=Attr1,Attr2;remain-constant=Attr3
            const sections = value.split(';');
            let totalMoved = 0;
            const results = [];
            const toVerify = []; // { name, target }
            sections
              .filter(section => section.indexOf('=') !== -1)
              .forEach(section => {
                const eqIndex = section.indexOf('=');
                const target = section.substring(0, eqIndex).trim();
                const names = section
                  .substring(eqIndex + 1)
                  .split(',')
                  .map(n => n.trim())
                  .filter(Boolean);
                if (!names.length) return;
                instance.proxy.$refs.varietyAttributes?.moveAttributes(names, target);
                totalMoved += names.length;
                results.push(`${names.length} to "${target}"`);
                names.forEach(name => toVerify.push({ name, target }));
              });
            if (!totalMoved)
              return '[Tool] Action failed: no valid attribute assignments were parsed from the token. Ask the user to clarify.';
            // Verify each attribute landed in the expected list
            const attrs = instance.proxy.$refs.varietyAttributes;
            const failed = [];
            if (attrs) {
              toVerify.forEach(({ name, target }) => {
                const lower = name.trim().toLowerCase();
                let list;
                if (target === 'remain-constant') list = attrs.remainConstantAttributes;
                else if (target === 'differ') list = attrs.canDifferAttributes;
                else list = attrs.unusedAttributes;
                if (!list?.some(a => a.name?.toLowerCase() === lower))
                  failed.push(`"${name}" → ${target}`);
              });
            }
            if (failed.length)
              return `[Tool] Action partially failed: these attributes could not be verified at their destination — ${failed.join(', ')}. Ask the user to check and try again.`;
            return `[Tool] Action succeeded: moved attributes — ${results.join(', ')}.`;
          },
        },
        'select-cann-group': {
          description:
            'Select a cann group on the variety groups page. ' +
            'Only use cann group names exactly as listed in the context. Only leaf-level cann group names are valid. ' +
            'Do not confirm success in your message text; the system will confirm or report errors after the action completes.',
          workingLabel: 'Selecting cann group',
          confirmationLabel: args => `select cann group "${args.value}"`,
          parameters: {
            type: 'object',
            properties: {
              value: {
                type: 'string',
                description: 'The exact name of the leaf-level cann group to select.',
              },
            },
            required: ['value'],
          },
          handler: async ({ value: name }) => {
            const cannGroups = instance.proxy.selectedScenario?.cannGroups;
            if (!cannGroups) return '[Tool] Action failed: no cann groups available.';
            const findLeafByName = nodes => {
              let result = null;
              nodes.some(node => {
                const isLeaf = !node.children || node.children.length === 0;
                if (isLeaf && node.name === name) {
                  result = node;
                  return true;
                }
                if (node.children?.length) {
                  result = findLeafByName(node.children);
                  if (result) return true;
                }
                return false;
              });
              return result;
            };
            const found = findLeafByName(cannGroups);
            if (!found)
              return `[Tool] Action failed: cann group "${name}" not found as a leaf-level group. Only leaf cann group names are valid for selection. Ask the user to clarify the name.`;
            instance.proxy.selectCannGroup({ key: found.key });
            await nextTick();
            if (!instance.proxy.$refs.varietyAttributes?.varietyGroups?.[found.key])
              return `[Tool] Action failed: cann group "${found.name}" was selected but the panel did not render correctly — the group data could not be found. Ask the user to try again.`;
            return `[Tool] Action succeeded: selected cann group "${found.name}".`;
          },
        },
        'save-variety-group': {
          description:
            'Save the attribute settings for the currently selected cann group only. ' +
            'Always save after making move-attribute changes and before selecting a different cann group. ' +
            'Do not confirm success in your message text; the system will confirm or report errors after the action completes.',
          workingLabel: 'Saving variety group',
          confirmationLabel: 'save variety group settings for the current cann group',
          parameters: { type: 'object', properties: {}, required: [] },
          handler: async () => {
            if (!canEditVarietyPage.value)
              return '[Tool] Action failed: you do not have permission to edit variety groups. Inform the user.';
            if (!instance.proxy.selectedCannGroup)
              return '[Tool] Action failed: no cann group is currently selected. Use select-cann-group first.';
            await instance.proxy.$refs.varietyAttributes?.saveOne();
            if (!instance.proxy.existsInVarietyGroups(instance.proxy.selectedCannGroup))
              return '[Tool] Action failed: the save did not complete successfully. The user has been notified. Ask them whether to retry.';
            return '[Tool] Action succeeded: saved variety settings for the current cann group.';
          },
        },
        'discard-variety-group': {
          description:
            'Discard unsaved attribute changes for the currently selected cann group, reverting to the last saved state. ' +
            'Use this to undo move-attribute changes before saving, or if the user wants to start over for a cann group. ' +
            'Do not confirm success in your message text; the system will confirm or report errors after the action completes.',
          workingLabel: 'Discarding variety group changes',
          confirmationLabel: 'discard unsaved variety group changes for the current cann group',
          parameters: { type: 'object', properties: {}, required: [] },
          handler: async () => {
            if (!canEditVarietyPage.value)
              return '[Tool] Action failed: you do not have permission to edit variety groups. Inform the user.';
            if (!instance.proxy.selectedCannGroup)
              return '[Tool] Action failed: no cann group is currently selected. Use select-cann-group first.';
            instance.proxy.$refs.varietyAttributes?.init();
            await nextTick();
            if (instance.proxy.$refs.varietyAttributes?.hasDataChanges)
              return '[Tool] Action failed: could not discard changes — the panel did not reset correctly. Ask the user to try again.';
            return '[Tool] Action succeeded: unsaved variety group changes discarded.';
          },
        },
        'save-all-variety-groups': {
          description:
            'Save ALL cann groups at once with their current attribute assignments — equivalent to the "Confirm as set" button. ' +
            'Use this when the user wants to skip variety group setup entirely, confirm all groups as-is with no attributes assigned, ' +
            'or bulk-confirm after the AI has finished editing all groups. ' +
            'Do NOT use this between individual cann group edits — use save-variety-group for that. ' +
            'Do not confirm success in your message text; the system will confirm or report errors after the action completes.',
          workingLabel: 'Saving all variety groups',
          confirmationLabel: 'save all variety group settings',
          parameters: { type: 'object', properties: {}, required: [] },
          handler: async () => {
            if (!canEditVarietyPage.value)
              return '[Tool] Action failed: you do not have permission to edit variety groups. Inform the user.';
            await instance.proxy.$refs.varietyAttributes?.saveAll();
            const leafCount = instance.proxy.getLeafCannGroupCount();
            const savedCount = instance.proxy.selectedScenario?.varietyGroups?.length ?? 0;
            if (leafCount > 0 && savedCount < leafCount)
              return '[Tool] Action failed: the save did not complete successfully. The user has been notified. Ask them whether to retry.';
            return '[Tool] Action succeeded: saved all variety group settings.';
          },
        },
      },
    });

    const {
      isUnsavedDataModalOpen,
      closeUnsavedDataModal,
      confirmUnsavedDataModal,
      beforeRouteLeave,
    } = useUnsavedDataWarning();
    return {
      canEditVarietyPage,
      aiPanelEnabled,
      formatScenarioProgress,
      isUnsavedDataModalOpen,
      closeUnsavedDataModal,
      confirmUnsavedDataModal,
      beforeRouteLeave,
    };
  },

  data() {
    return { selectedCannGroup: null };
  },

  computed: {
    ...mapState(useScenariosStore, ['selectedScenario']),
    ...mapState(useAiStore, ['isSidePanelOpen']),

    hasDataChanges() {
      // AOV3-405 required for variety-groups unsaved data warning
      return this.$refs.varietyAttributes.hasDataChanges;
    },
    hasDataErrors() {
      // required for <page-actions> component
      return false;
    },

    isEditingDisabled() {
      return !this.canEditVarietyPage;
    },
  },

  watch: {},

  mounted() {
    const aiStore = useAiStore();
    const contextDisplay = [this.selectedScenario?.name, 'Variety Groups'].filter(Boolean);
    aiStore.enableSidePanel({ section: 'variety-groups', contextDisplay });
    aiStore.setSidePanelContextFactory(() => this.buildInnerContext());
    // Safe to set immediately: ScenarioWorkflow's v-if gate ensures selectedScenario
    // (including cannGroups/varietyGroups) is fully populated before this page mounts.
    aiStore.setContextReady(true);
  },

  methods: {
    selectCannGroup({ key }) {
      this.selectedCannGroup = key;
    },

    existsInVarietyGroups(key) {
      return this.selectedScenario.varietyGroups.some(({ cannGroupId }) => key === cannGroupId);
    },

    saveAll() {
      this.$refs.varietyAttributes.saveAll();
    },

    /**
     * Build the inner context object for the AI assistant (called lazily at send-time).
     */
    buildInnerContext() {
      const configuredGroups = this.selectedScenario?.varietyGroups?.length || 0;
      const totalLeafGroups = this.getLeafCannGroupCount();
      const varietyGroupsStatus = { configured: configuredGroups, total: totalLeafGroups };

      const currentCannGroup = this.selectedScenario?.cannGroups
        ? this.findCannGroupByKey(this.selectedScenario.cannGroups, this.selectedCannGroup)
        : null;

      let varietySettings = null;
      if (currentCannGroup && this.$refs.varietyAttributes) {
        const attrs = this.$refs.varietyAttributes;
        const hasChanges = attrs.hasDataChanges || false;
        varietySettings = {
          constantAttributes: attrs.remainConstantAttributes?.map(a => a.name).join(', ') || 'None',
          differingAttributes: attrs.canDifferAttributes?.map(a => a.name).join(', ') || 'None',
          unusedAttributes: attrs.unusedAttributes?.map(a => a.name).join(', ') || 'None',
          hasUnsavedChanges: hasChanges,
        };
      }

      const availableAttributes =
        this.selectedScenario?.customAttributes?.map(a => a.name).join(', ') || 'None';

      return {
        scenarioId: this.selectedScenario?._id,
        scenarioName: this.selectedScenario?.name || 'No scenario selected',
        availableAttributes,
        cannGroupName: currentCannGroup?.name || 'No Cann Group selected',
        scenarioProgress: this.formatScenarioProgress,
        varietyGroupsStatus,
        currentCannGroupSettings: varietySettings
          ? {
              constantAttributes: varietySettings.constantAttributes,
              differingAttributes: varietySettings.differingAttributes,
              unusedAttributes: varietySettings.unusedAttributes,
              hasUnsavedChanges: varietySettings.hasUnsavedChanges,
            }
          : null,
      };
    },

    getLeafCannGroupCount() {
      if (!this.selectedScenario?.cannGroups) return 0;
      const getLeafNodes = (nodes, count = 0) =>
        nodes.reduce((acc, node) => {
          if (!node.children || node.children.length === 0) return acc + 1;
          return getLeafNodes(node.children, acc);
        }, count);
      return getLeafNodes(this.selectedScenario.cannGroups);
    },

    findCannGroupByKey(nodes, key) {
      let result = null;
      nodes.some(node => {
        if (node.key === key) {
          result = node;
          return true;
        }
        if (node.children && node.children.length > 0) {
          result = this.findCannGroupByKey(node.children, key);
          if (result) return true;
        }
        return false;
      });
      return result;
    },
  },
};
</script>

<style lang="scss">
@import '@style/base/_variables.scss';
.variety-container {
  color: $assortment-text-colour;
  font-size: 12px;
  font-weight: 600;
  min-width: 100%;
  height: 100%;
  .header-container {
    span {
      font-size: 1.2rem;
    }
  }
  .variety-attributes {
    border-right: $assortment-border-colour 1px solid;
    &-title {
      border-bottom: $assortment-border-colour 1px solid;

      .col {
        padding: 15px;
      }
    }
  }
  .main-panel {
    border-top: $assortment-border-colour 1px solid;
    height: $tab-panel-height;
  }

  .variety-attributes-container {
    flex: 1;
    height: 300px;
    position: relative;
  }

  .page-actions-container {
    border-top: 1px solid $assortment-panel-border-divider-colour;
  }
}
</style>
